/*
 * ShareChannels — Reusable channel-button row.
 * =============================================
 *
 * Drop into any share modal / sheet to expose the same buttons across
 * every surface. Wires directly into ShareService — no local state,
 * no duplicated deep-link URLs, no per-screen implementations.
 *
 * Usage:
 *   <ShareChannels
 *     blob={bakedBlob}
 *     filename="rgm-track-abc.png"
 *     title="Midnight Bloom — MC Radiant"
 *     text="🎧 New drop on RGM Studio"
 *     url="https://rgmstudio.org/api/share/track/sub_123"
 *     channels={["native","copy","download","whatsapp","x","facebook","threads","instagram","messenger","sms","email"]}
 *     onEvent={(channel, status) => track(channel, status)}
 *   />
 *
 * All buttons are always rendered unless a channel is explicitly
 * filtered out via `channels`. The component still calls the backend
 * fire-and-forget "share recorded" endpoint through the `onEvent` hook
 * (callers wire that in — this component stays UI-only).
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ShareService from "@/lib/ShareService";

const DEFAULT_CHANNELS = [
  "native",
  "copy",
  "download",
  "whatsapp",
  "x",
  "facebook",
  "threads",
  "instagram",
  "messenger",
  "sms",
  "email",
];

// Channel definitions — icon + label + background style. Order in the
// row follows DEFAULT_CHANNELS above.
const CHANNEL_META = {
  native:    { label: "Share",     icon: "📲",  cls: "bg-gradient-to-r from-purple-600 to-pink-600 text-white" },
  copy:      { label: "Copy Link", icon: "🔗",  cls: "bg-white/10 text-white border border-white/20 hover:bg-white/15" },
  download:  { label: "Download",  icon: "⬇",   cls: "bg-white/10 text-white border border-white/20 hover:bg-white/15" },
  whatsapp:  { label: "WhatsApp",  icon: "🟢",  cls: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  x:         { label: "X",         icon: "✕",   cls: "bg-black text-white border border-white/20 hover:bg-black/80" },
  facebook:  { label: "Facebook",  icon: "f",   cls: "bg-blue-600 hover:bg-blue-700 text-white" },
  threads:   { label: "Threads",   icon: "@",   cls: "bg-black text-white border border-white/20 hover:bg-black/80" },
  instagram: { label: "Instagram", icon: "📷",  cls: "bg-gradient-to-tr from-fuchsia-500 via-pink-500 to-amber-500 text-white" },
  messenger: { label: "Messenger", icon: "💬",  cls: "bg-sky-500 hover:bg-sky-600 text-white" },
  sms:       { label: "SMS",       icon: "✉",   cls: "bg-slate-600 hover:bg-slate-700 text-white" },
  email:     { label: "Email",     icon: "✉",   cls: "bg-slate-500 hover:bg-slate-600 text-white" },
};

const ShareChannels = ({
  blob,
  filename = "rgm-share.png",
  title = "",
  text = "",
  url = "",
  channels = DEFAULT_CHANNELS,
  onEvent,
  testIdPrefix = "share",
  messengerAppId,
  compact = false,
}) => {
  const [busy, setBusy] = useState(null);

  const supportsNative = useMemo(() => ShareService.canNativeShare(), []);
  const supportsNativeFiles = useMemo(() => ShareService.canNativeShareFiles(), []);
  const hasClipboard = useMemo(() => ShareService.hasClipboard(), []);

  const emit = (channel, status) => {
    try { if (onEvent) onEvent(channel, status); } catch { /* ignore */ }
  };

  const withBusy = async (key, fn, successMsg, failMsg) => {
    setBusy(key);
    try {
      const ok = await fn();
      if (ok === false || ok === "failed") {
        toast.error(failMsg || "Couldn't share — try Download instead.");
        emit(key, "failed");
      } else if (ok === "cancelled") {
        emit(key, "cancelled");
      } else {
        if (successMsg) toast.success(successMsg);
        emit(key, "shared");
      }
    } catch (e) {
      toast.error(failMsg || `Couldn't ${key}`);
      emit(key, "failed");
    } finally {
      setBusy(null);
    }
  };

  const handlers = {
    native: () => withBusy(
      "native",
      async () => await ShareService.share({ blob, filename, title, text, url }),
      null,
      "Sharing isn't available on this device — try Copy Link.",
    ),
    copy: () => withBusy(
      "copy",
      async () => await ShareService.copyLink(url),
      "Link copied!",
      "Couldn't copy — long-press to select the link.",
    ),
    download: () => withBusy(
      "download",
      () => ShareService.download(blob, filename),
      "Saved to your device 🎉",
      "Download failed — try again.",
    ),
    whatsapp:  () => withBusy("whatsapp",  () => ShareService.whatsapp({ text, url }),  null, "WhatsApp is unavailable — try Copy Link."),
    x:         () => withBusy("x",         () => ShareService.x({ text, url }),          null, "X is unavailable — try Copy Link."),
    facebook:  () => withBusy("facebook",  () => ShareService.facebook({ url }),         null, "Facebook is unavailable — try Copy Link."),
    threads:   () => withBusy("threads",   () => ShareService.threads({ text, url }),    null, "Threads is unavailable — try Copy Link."),
    instagram: () => withBusy(
      "instagram",
      async () => await ShareService.instagram({ blob, filename }),
      "Image saved — open Instagram and upload it to your Story 📸",
      "Save failed — try Download instead.",
    ),
    messenger: () => withBusy("messenger", () => ShareService.messenger({ url, appId: messengerAppId }), null, "Messenger is unavailable — try Copy Link."),
    sms:       () => withBusy("sms",       () => ShareService.sms({ text, url }),         null, "SMS is unavailable — try Copy Link."),
    email:     () => withBusy("email",     () => ShareService.email({ subject: title, body: text, url }), null, "Email is unavailable — try Copy Link."),
  };

  // Filter out channels the environment can't support.
  const visible = channels.filter((c) => {
    if (c === "native")   return supportsNative || supportsNativeFiles;
    if (c === "copy")     return hasClipboard || true; // fallback always OK
    if (c === "download") return !!blob;
    if (c === "instagram")return !!blob;
    // messenger without appId still works via mobile URI, so we keep it.
    return true;
  });

  return (
    <div className={`flex flex-wrap gap-2 ${compact ? "justify-center" : ""}`}
         data-testid={`${testIdPrefix}-channels`}>
      {visible.map((c) => {
        const m = CHANNEL_META[c];
        if (!m) return null;
        const disabled = busy === c || (c === "download" && !blob) || (c === "native" && !supportsNative && !supportsNativeFiles);
        return (
          <Button
            key={c}
            type="button"
            disabled={disabled}
            onClick={handlers[c]}
            data-testid={`${testIdPrefix}-${c}`}
            className={`text-xs sm:text-sm font-semibold px-3 py-2 ${m.cls} disabled:opacity-50`}
          >
            <span className="mr-1.5 font-black inline-block min-w-4 text-center">{m.icon}</span>
            {m.label}
          </Button>
        );
      })}
    </div>
  );
};

export default ShareChannels;
export { CHANNEL_META, DEFAULT_CHANNELS };
